import {
  checkArgument,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {getFirstElement} from 'external/dev_april_corgi+/js/common/collections';

import {getLabwareSettings} from '../types/labware_settings';
import {WellLocation, WellSlot} from './opentrons';
import {OpentronsFlexBay} from './robots';

interface BayCapabilities {
  magnetic?: boolean;
  staging?: boolean;
  temperature?: boolean;
  thermocycling?: boolean;
  wellVolumeNl?: number;
}

export interface Well {
  position: {row: number; col: number};
  slot: WellSlot;
  volumeNl: number;
}

type ModuleKind = 'magnetic' | 'temperature' | 'thermocycling';

export interface Plate {
  bay: OpentronsFlexBay;
  lid:
    | {
        bay: OpentronsFlexBay;
        model: string;
      }
    | undefined;
  maxWellVolumeNl: number;
  module:
    | {
        bay: OpentronsFlexBay;
        type: ModuleKind;
      }
    | undefined;
  specification: {
    model: string;
    dimensions: {xMm: number; yMm: number; zMm: number};
    layout: {rows: number; columns: number};
    volumeNlToHeightMm: (nl: number) => number;
  };
  availableWells: Well[];
  wellCount: number;
}

interface TipRack {
  bay: OpentronsFlexBay;
  clean: Array<Omit<Well, 'volumeNl'>>;
  model: string;
}

type DeckItem =
  | {kind: 'blocked'}
  | {
      kind: 'labware';
      model: string;
      regularWells: {columns: number; rows: number} | undefined;
    }
  | {kind: 'module'; module: ModuleKind}
  | {kind: 'pending_load'}
  | {kind: 'tip_rack'; model: string}
  | {kind: 'tip_trash'}
  | {kind: 'trash'};

const MAGNETIC_BAYS: OpentronsFlexBay[] = ['C2']; // just the on magnet slot
const STAGING_BAYS: OpentronsFlexBay[] = ['A4', 'B4', 'C4', 'D4'];
const TEMPERATURE_BAYS: OpentronsFlexBay[] = ['C1', 'D1'];
const THERMOCYCLING_BAYS: OpentronsFlexBay[] = ['B1'];
const TALL_BAYS: OpentronsFlexBay[] = ['A3'];

export class OpentronsDeck {
  private readonly bays: Map<OpentronsFlexBay, DeckItem[]>;
  private readonly _tipRacks: TipRack[];
  private readonly tipTrashes: Map<OpentronsFlexBay, OpentronsFlexBay>;

  constructor(private readonly sandbox: boolean) {
    this.bays = new Map(
      (
        [
          // This is transposed so we offer the left column (*1 bays) first since they're easier to
          // access
          'A1',
          'B1',
          'C1',
          'D1',
          'A2',
          'B2',
          'C2',
          'D2',
          'A3',
          'B3',
          'C3',
          'D3',
          'A4',
          'B4',
          'C4',
          'D4',
        ] as const
      ).map(b => [b, []]),
    );
    checkExists(this.bays.get('A1')).push({kind: 'blocked'}); // Blocked by thermocycler
    checkExists(this.bays.get('A2')).push({
      kind: 'tip_rack',
      model: 'opentrons_flex_96_filtertiprack_50ul',
    });
    checkExists(this.bays.get('B3')).push({
      kind: 'tip_rack',
      model: 'opentrons_flex_96_filtertiprack_50ul',
    });
    checkExists(this.bays.get('C3')).push({
      kind: 'tip_rack',
      model: 'opentrons_flex_96_filtertiprack_50ul',
    });
    checkExists(this.bays.get('D3')).push({kind: 'trash'});

    this._tipRacks = [];
    for (const [bay, stack] of this.bays.entries()) {
      const top = stack[stack.length - 1];
      if (top?.kind === 'tip_rack') {
        this._tipRacks.push({
          bay,
          clean: makeSpaces([{rows: 8, columns: 12}], {frontToBack: true}),
          model: top.model,
        });
      }
    }
    this.tipTrashes = new Map(
      [...this.bays]
        .filter(
          ([, stack]) =>
            stack.length > 0 && stack[stack.length - 1].kind === 'tip_trash',
        )
        .map(([bay, _], i) => [this._tipRacks[i].bay, bay] as const),
    );
  }

  exposeSlots(): Map<OpentronsFlexBay, Readonly<DeckItem[]>> {
    return new Map(this.bays);
  }

  findFreeBay(capabilities: BayCapabilities): {
    bay: OpentronsFlexBay;
    module: 'magnetic' | 'temperature' | 'thermocycling' | undefined;
  } {
    const options = new Set(
      [...this.bays]
        .filter(([, stack]) => stack.length === 0)
        .map(([bay]) => bay),
    );
    const modules = new Set<'magnetic' | 'temperature' | 'thermocycling'>();
    for (const [module, test, matches] of [
      ['magnetic', capabilities.magnetic, MAGNETIC_BAYS],
      [undefined, capabilities.staging, STAGING_BAYS],
      ['temperature', capabilities.temperature, TEMPERATURE_BAYS],
      ['thermocycling', capabilities.thermocycling, THERMOCYCLING_BAYS],
      [undefined, (capabilities.wellVolumeNl ?? 0) > 200_000, TALL_BAYS],
    ] as const) {
      if (test) {
        if (module) {
          modules.add(module);
        }
        for (const bay of [...options]) {
          if (matches.indexOf(bay) < 0) {
            options.delete(bay);
          }
        }
      } else {
        for (const bay of matches) {
          options.delete(bay);
        }
      }
    }

    if (options.size === 0) {
      throw new Error(
        `No available slot matching the given capabilities: ${JSON.stringify(capabilities)}`,
      );
    }
    if (modules.size > 1) {
      throw new Error('Only 0 or 1 modules may be used per plate');
    }

    const choice = getFirstElement(options);
    checkExists(this.bays.get(choice)).push({kind: 'pending_load'});
    const module = modules[Symbol.iterator]().next().value;
    return {bay: choice, module};
  }

  allocatePlate(capabilities: BayCapabilities): Plate {
    const {bay, module} = this.findFreeBay(capabilities);
    if (module) {
      const stack = checkExists(this.bays.get(bay));
      if (
        stack.length >= 1 &&
        stack[stack.length - 1].kind === 'pending_load'
      ) {
        stack.pop();
      }
      stack.push({kind: 'module', module});
    }

    const settings = getLabwareSettings();
    let lid;
    let maxWellVolumeNl;
    let plate;

    if ((capabilities.wellVolumeNl ?? 0) > 200_000) {
      checkArgument(
        !capabilities.magnetic &&
          !capabilities.temperature &&
          !capabilities.thermocycling,
        'Deep-well plates are not compatible with any modules',
      );
      maxWellVolumeNl = 2_000_000;
      plate = settings.deepWellPlate;
      if (settings.lid.model) {
        lid = {bay, model: settings.lid.model};
      }
    } else if (capabilities.thermocycling) {
      maxWellVolumeNl = 200_000;
      plate = settings.pcrPlate;
      // No lid for thermocycling plates
    } else {
      maxWellVolumeNl = 200_000;
      plate = settings.pcrPlate;
      if (settings.lid.model) {
        lid = {bay, model: settings.lid.model};
      }
    }

    const layout = plate.layout;
    const model = plate.model;
    const availableWells = makeSpaces([layout], {rightToLeft: true});

    return {
      bay,
      lid,
      maxWellVolumeNl,
      module: module ? {bay, type: module} : undefined,
      specification: {
        model,
        dimensions: {
          xMm: 127.76,
          yMm: 85.48,
          zMm: plate.heightMm,
        },
        layout,
        volumeNlToHeightMm: () => 0,
      },
      availableWells,
      wellCount: availableWells.length,
    };
  }

  allocateTips(channels: number): WellLocation & {bay: OpentronsFlexBay} {
    for (const rack of this._tipRacks) {
      for (let i = 0; i < rack.clean.length - channels + 1; i++) {
        const root = rack.clean[i].position;
        const startCol = root.col;
        const startRow = root.row;
        let matches = true;
        for (let j = 1; j < channels; ++j) {
          const well = rack.clean[i + j].position;
          if (startCol !== well.col || startRow + j !== well.row) {
            matches = false;
            break;
          }
        }

        if (!matches) {
          continue;
        }

        // Say we are doing a 3-channel pick-up with tips in A1-C1 and E1-F1. We skip E1-F1 because
        // not enough tips, and then A1-C1 looks good. But when we do a partial pickup on A1 the
        // pipette will overlap E1-F1 and pick them up too. So what we need to do is verify that
        // there are no tips before us in the same column
        const previous = rack.clean[i - 1];
        matches = previous?.position?.col !== startCol;

        if (matches) {
          const tips = rack.clean.splice(i, channels);
          return {
            bay: rack.bay,
            well: tips[tips.length - 1].slot, // we are picking up in reverse so flip it here
          };
        }
      }
    }

    throw new Error(`Cannot allocate ${channels} tips`);
  }

  loadLabware(into: OpentronsFlexBay, model: string): void {
    const stack = checkExists(this.bays.get(into));
    if (stack.length >= 1 && stack[stack.length - 1].kind === 'pending_load') {
      stack.pop();
    }

    // All 96-well plates have 8 rows x 12 columns
    const regularWells = model.includes('96')
      ? {rows: 8, columns: 12}
      : undefined;
    stack.push({kind: 'labware', model, regularWells});
  }

  moveLabware(
    from: OpentronsFlexBay,
    to: OpentronsFlexBay,
    depth: number,
  ): void {
    const fromStack = checkExists(this.bays.get(from));
    const items = [];
    for (let i = 0; i < depth; ++i) {
      const fromTop = checkExists(fromStack.pop());
      checkArgument(fromTop.kind === 'labware');
      items.push(fromTop);
    }
    items.reverse();
    const toStack = checkExists(this.bays.get(to));
    if (
      toStack.length >= 1 &&
      toStack[toStack.length - 1].kind === 'pending_load'
    ) {
      toStack.pop();
    }
    // TODO(april): check something here
    toStack.push(...items);
  }

  tipRacks(): Array<{bay: OpentronsFlexBay; model: string}> {
    return this._tipRacks;
  }

  tipTrashBays(): Map<OpentronsFlexBay, OpentronsFlexBay> {
    return this.tipTrashes;
  }
}

function makeSpaces(
  sections: Readonly<Array<{rows: number; columns: number}>>,
  options:
    | {
        frontToBack?: boolean;
        rightToLeft?: boolean;
      }
    | undefined,
): Well[] {
  let offset = 0;
  const spots: Well[] = [];
  for (const {rows, columns} of sections) {
    const rowNames = options?.frontToBack
      ? 'ZYXWVUTSRQPONMLKJIHGFEDCBA'.slice(26 - rows)
      : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.slice(0, rows);
    checkArgument(rows === rowNames.length);
    spots.push(
      ...[...Array(columns).keys()].flatMap(col =>
        [...rowNames.split('')].map((c, row) => ({
          position: {row, col},
          slot: `${c}${col + 1 + offset}` as WellSlot,
          volumeNl: 0,
        })),
      ),
    );
    offset += columns;
  }
  if (options?.rightToLeft) {
    spots.reverse();
  }
  return spots;
}

